import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, Button, Typography, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { useStyles } from '../styles';
import { ReactComponent as SortIcon } from 'assets/icons/SortIcon.svg';
import { DropdownIcon } from 'assets/svgComponents/DropdownIcon';
import { SortMenu } from './SortMenu';
import { SortType } from '../types';

interface CourseTabsProps {
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  courseCounts: {
    active: number;
    expired: number;
  };
  onSortChange?: (sortType: SortType) => void;
  sortType?: SortType;
}

export const CourseTabs: React.FC<CourseTabsProps> = ({
  activeTab,
  onTabChange,
  courseCounts,
  onSortChange,
  sortType,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [tabAnchorEl, setTabAnchorEl] = useState<null | HTMLElement>(null);

  const createTabLabel = (text: string, count: number) => (
    <Box className={classes.customTabLabel}>
      <Typography component="span">{text}</Typography>
      <Box className={`${classes.customCountBadge} custom-count-badge`}>{count < 9 ? `0${count}` : count}</Box>
    </Box>
  );

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (sortType: SortType) => {
    if (onSortChange) {
      onSortChange(sortType);
    }
  };

  const handleTabMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setTabAnchorEl(event.currentTarget);
  };

  const handleTabMenuClose = () => {
    setTabAnchorEl(null);
  };

  const handleTabMenuItemClick = (value: number) => {
    onTabChange({} as React.SyntheticEvent, value);
    handleTabMenuClose();
  };

  const tabOptions = [
    { value: 0, key: 'active', label: t('courses.tabs.active'), count: courseCounts.active },
    { value: 1, key: 'expired', label: t('courses.tabs.expired'), count: courseCounts.expired },
  ];

  return (
    <Box className={classes.tabsContainer} data-testid="courses-tabs-container">
      {isMobile ? (
        <>
          <Button
            onClick={handleTabMenuClick}
            className={classes.tabDropdown}
            data-testid="courses-tab-dropdown-button"
            endIcon={<DropdownIcon />}
          >
            {tabOptions.find(option => option.value === activeTab)?.label}
          </Button>
          <Menu
            anchorEl={tabAnchorEl}
            open={Boolean(tabAnchorEl)}
            onClose={handleTabMenuClose}
            className={classes.dropdownMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {tabOptions.map(option => (
              <MenuItem
                key={option.value}
                data-testid={`courses-tab-dropdown-item-${option.key}`}
                onClick={() => handleTabMenuItemClick(option.value)}
                className={classes.dropdownMenuItem}
                selected={activeTab === option.value}
              >
                <Box className={classes.dropdownItemContent}>
                  <Typography component="span">{option.label}</Typography>
                  {/* <Box className={classes.dropdownCountBadge}>
                    {option.count < 9 ? `0${option.count}` : option.count}
                  </Box> */}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <Tabs value={activeTab} onChange={onTabChange} className={classes.tabs} data-testid="courses-tabs">
          <Tab
            label={createTabLabel(t('courses.tabs.active'), courseCounts.active)}
            className={classes.tab}
            data-testid="courses-tab-active"
          />
          <Tab
            label={createTabLabel(t('courses.tabs.expired'), courseCounts.expired)}
            className={classes.tab}
            data-testid="courses-tab-expired"
          />
        </Tabs>
      )}
      <Button
        startIcon={isMobile ? undefined : <SortIcon />}
        className={classes.sortButton}
        data-testid="courses-sort-button"
        onClick={handleSortClick}
      >
        {isMobile ? <SortIcon /> : t('courses.actions.sort')}
      </Button>
      <SortMenu
        anchorEl={sortAnchorEl}
        onClose={handleSortClose}
        onSortChange={handleSortChange}
        currentSort={sortType}
      />
    </Box>
  );
};
